import {Task} from "../../api";

/** Sortierkriterien der Task-Übersicht – identische Bedienung wie in den myCheck-Übersichten. */
export type TaskSortField = 'name' | 'updated' | 'priority' | 'state';
export type TaskSortDirection = 'asc' | 'desc';

export interface TaskSortFieldOption {
  field: TaskSortField;
  label: string;
  ascLabel: string;
  descLabel: string;
}

export const TASK_SORT_FIELD_OPTIONS: TaskSortFieldOption[] = [
  {field: 'name', label: 'Name', ascLabel: 'A–Z', descLabel: 'Z–A'},
  {field: 'updated', label: 'Zuletzt geändert', ascLabel: 'Älteste zuerst', descLabel: 'Neueste zuerst'},
  {field: 'priority', label: 'Priorität', ascLabel: 'Hoch zuerst', descLabel: 'Niedrig zuerst'},
  {field: 'state', label: 'Status', ascLabel: 'Offen zuerst', descLabel: 'Verifiziert zuerst'},
];

/** Reihenfolge für die Sortierung nach Priorität: hoch zuerst. */
function priorityRank(priority: Task.PriorityEnum | undefined): number {
  const order: Record<string, number> = {HIGH: 0, NORMAL: 1, LOW: 2};
  return priority ? order[priority] ?? 3 : 3;
}

/** Reihenfolge für die Sortierung nach Status: entlang des Bearbeitungsfortschritts. */
function stateRank(state: Task.StateEnum | undefined): number {
  const order: Record<string, number> = {OPEN: 0, PROCESS: 1, DONE: 2, VERIFIED: 3};
  return state ? order[state] ?? 4 : 4;
}

/**
 * Änderungszeitpunkt einer Aufgabe. `modifiedDate`/`creationDate` liefert das Backend
 * (siehe TaskService#saveTask), die Felder werden hier aber bewusst dynamisch gelesen: der
 * API-Client unter `src/app/api` ist generiert und trägt sie erst nach der nächsten Generierung.
 * Bestandsaufgaben ohne Zeitstempel landen am Ende.
 */
function modifiedTime(task: Task): number {
  const raw = task as unknown as Record<string, unknown>;
  const value = raw['modifiedDate'] ?? raw['creationDate'];
  if (!value || (typeof value !== 'string' && !(value instanceof Date))) {
    return 0;
  }
  const time = new Date(value as string | Date).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function titleOf(task: Task): string {
  return task.title ?? '';
}

/**
 * Sortiert eine Aufgabenliste stabil nach dem gewählten Kriterium. Bei Gleichstand entscheidet
 * immer der Titel, damit dieselbe Liste nach einer Änderung (z.B. Zuweisung) gleich bleibt.
 */
export function sortTasks(tasks: Task[], field: TaskSortField, direction: TaskSortDirection): Task[] {
  const sorted = [...tasks];
  const dir = direction === 'asc' ? 1 : -1;
  const byTitle = (a: Task, b: Task) => titleOf(a).localeCompare(titleOf(b), 'de');

  switch (field) {
    case 'updated':
      sorted.sort((a, b) => (modifiedTime(a) - modifiedTime(b)) * dir || byTitle(a, b));
      break;
    case 'priority':
      sorted.sort((a, b) => (priorityRank(a.priority) - priorityRank(b.priority)) * dir || byTitle(a, b));
      break;
    case 'state':
      sorted.sort((a, b) => (stateRank(a.state) - stateRank(b.state)) * dir || byTitle(a, b));
      break;
    default:
      sorted.sort((a, b) => byTitle(a, b) * dir);
  }

  return sorted;
}
